import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, NumberFormatter } from '@mantine/core';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import AppText from '../../common/AppText';
import PaginationList from '../../common/PaginationList';
import AppFlexbox from '../../common/AppFlexbox';
import { singularPluralFormat } from '../../../helpers/format';
import AppCard from '../../common/AppCard';
import AppImage from '../../common/AppImage';
import { triggerNotification } from '../../../helpers/notification';

const AdminProductMergeModal = ({
  isOpen,
  onClose,
  onSuccess,
  ecomExternalProducts
}) => {
  const [formState, setFormState] = useState({
    selectedPkEcomExternalProduct: null,
    showConfirm: false,
    loading: false
  });
  const { state, mergeEcomExternalProducts } = useContext(AdminContext);
  const selectedProduct = ecomExternalProducts?.find(
    (p) => p.pkEcomExternalProduct === formState.selectedPkEcomExternalProduct
  );
  const connectionType =
    ecomExternalProducts?.[0]?.fkEcomExternalConnectionType;
  const allProductsFromSameConnectionType = ecomExternalProducts?.every(
    (p) => p.fkEcomExternalConnectionType === connectionType
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        selectedPkEcomExternalProduct: null,
        showConfirm: false,
        loading: false
      });
    }
  }, [isOpen]);

  const onMergeProductVariants = () => {
    setFormState({ ...formState, loading: true });
    mergeEcomExternalProducts(
      {
        fkEcomExternalConnectionType: connectionType,
        pkEcomExternalProduct: formState.selectedPkEcomExternalProduct,
        pkEcomExternalProducts: ecomExternalProducts
          .map((p) => p.pkEcomExternalProduct)
          .filter((f) => f !== formState.selectedPkEcomExternalProduct)
      },
      () => {
        triggerNotification('Products merged', 'success');
        setFormState({ ...formState, loading: false });
        onSuccess();
      },
      (e) => {
        triggerNotification(e);
        setFormState({ ...formState, loading: false });
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showConfirm
          ? {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: onMergeProductVariants,
              submitTitle: 'Yes, merge variants'
            }
          : {
              onCancel: onClose,
              isLoading: formState.loading,
              onSubmit: () => {
                setFormState({ ...formState, showConfirm: true });
              },
              isSubmitDisabled: !formState.selectedPkEcomExternalProduct,
              submitTitle: 'Continue'
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      title="Group product variants"
    >
      <AppStack style={{ gap: 16 }}>
        {!allProductsFromSameConnectionType ? (
          <AppText
            style={{ fontSize: 14, color: '#C40000', textAlign: 'center' }}
          >
            All products must be from the same provider to merge variants.
          </AppText>
        ) : formState.showConfirm ? (
          <>
            {selectedProduct && (
              <AppCard style={{ padding: 8 }} withBorder>
                <AppFlexbox>
                  <AppStack>
                    <AppImage
                      src={selectedProduct.previewImage}
                      style={{
                        width: 50,
                        height: 50,
                        border: 'solid px lightgrey'
                      }}
                    />
                  </AppStack>

                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 16, fontWeight: 500 }}>
                      {selectedProduct.name}
                    </AppText>
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      {singularPluralFormat(
                        selectedProduct.variantCount,
                        'variant',
                        'variants'
                      )}
                    </AppText>
                  </AppStack>
                </AppFlexbox>
              </AppCard>
            )}

            <AppStack style={{ gap: 10 }}>
              <AppText style={{ fontSize: 16 }}>
                Are you sure you want to merge all variants into this product?
              </AppText>

              <AppText style={{ fontSize: 14, color: '#666' }}>
                The other products will be deleted and their variants will be
                transfered.
              </AppText>

              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                This action cannot be undone.
              </AppText>
            </AppStack>
          </>
        ) : (
          <>
            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              Select product you want to merge all the variants into.
            </AppText>

            <PaginationList
              emptyMessage="No products found."
              items={(ecomExternalProducts ?? [])
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => {
                  const selected =
                    formState.selectedPkEcomExternalProduct ===
                    p.pkEcomExternalProduct;

                  return (
                    <AppFlexbox
                      key={p.pkEcomExternalProduct}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          selectedPkEcomExternalProduct: selected
                            ? null
                            : p.pkEcomExternalProduct
                        });
                      }}
                      style={{
                        gap: 8,
                        padding: 8,
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        disabled={formState.loading}
                        onChange={() => {}}
                      />
                      <AppFlexbox
                        style={{
                          flex: 1,
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: 500
                            }}
                          >
                            {p.name}
                          </AppText>

                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {p.model}
                          </AppText>

                          <AppText
                            style={{
                              fontSize: 14,
                              color: '#666'
                            }}
                          >
                            {singularPluralFormat(
                              p.variantCount,
                              'variant',
                              'variants'
                            )}
                          </AppText>
                        </AppStack>

                        <AppFlexbox>
                          <AppText style={{ fontSize: 14, color: '#666' }}>
                            {p.minPrice === p.maxPrice ? (
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                thousandSeparator
                                value={p.maxPrice / 100}
                              />
                            ) : (
                              <>
                                <NumberFormatter
                                  decimalScale={2}
                                  fixedDecimalScale
                                  prefix="$"
                                  thousandSeparator
                                  value={p.minPrice / 100}
                                />{' '}
                                -{' '}
                                <NumberFormatter
                                  decimalScale={2}
                                  fixedDecimalScale
                                  prefix="$"
                                  thousandSeparator
                                  value={p.maxPrice / 100}
                                />
                              </>
                            )}
                          </AppText>
                        </AppFlexbox>
                      </AppFlexbox>
                    </AppFlexbox>
                  );
                })}
              itemsPerPage={10}
              onPageChange={(pageIndex) =>
                setFormState({
                  ...formState,
                  pageIndex
                })
              }
              pageIndex={formState.pageIndex}
            />
          </>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

AdminProductMergeModal.propTypes = {
  ecomExternalProducts: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};

export default AdminProductMergeModal;
